<script setup lang="ts">
import type { BrandOption, ItemEditState, SolTagPresentation } from './revalidationsShared.types'
import type { RevalidationItem } from '~/store-v2/revalidations/revalidationList.types'
import { SolCheckbox, SolInputText, SolSelect, SolTag, SolTooltip } from '@solfacil/girassol'
import IconArrowForward from '~icons/material-symbols/arrow-forward'
import IconContentPaste from '~icons/material-symbols/content-paste'
import RevalidationSerialInput from './RevalidationSerialInput.vue'

defineProps<{
  item: RevalidationItem
  index: number
  rowKey: string
  checked: boolean
  checkboxDisabled?: boolean
  checkboxTooltip?: string
  editState: ItemEditState
  brandOptions: BrandOption[]
  brandOtherValue: string
  statusTag: SolTagPresentation
  portal: { name: string, href: string | null }
  disabled?: boolean
}>()

const emit = defineEmits<{
  toggle: []
  updateEdit: [patch: Partial<ItemEditState>]
  submit: []
}>()

function onBrandUpdate(value: unknown) {
  const raw = Array.isArray(value) ? value[0] : value
  emit('updateEdit', { brand: raw as BrandOption | undefined })
}

function onCustomBrandUpdate(value: string) {
  emit('updateEdit', { customBrand: value })
}

function onSerialsUpdate(value: string[]) {
  emit('updateEdit', { serials: value })
}

function onPlantNameUpdate(value: string) {
  emit('updateEdit', { plantName: value })
}

async function handlePaste() {
  try {
    const text = await navigator.clipboard.readText()
    if (text.trim())
      emit('updateEdit', { plantName: text.trim() })
  }
  catch {}
}
</script>

<template>
  <li class="revalidation-item" :class="{ 'revalidation-item--disabled': disabled }">
    <div class="item-checkbox-area">
      <SolTooltip
        v-if="checkboxTooltip"
        :text="checkboxTooltip"
        position="top"
      >
        <span class="item-checkbox-tooltip-wrap">
          <SolCheckbox
            :id="`revalidation-item-${index}`"
            :name="`revalidation-item-${index}`"
            :value="rowKey"
            :checked="checked"
            :disabled="checkboxDisabled"
            @change="emit('toggle')"
          />
        </span>
      </SolTooltip>
      <SolCheckbox
        v-else
        :id="`revalidation-item-${index}`"
        :name="`revalidation-item-${index}`"
        :value="rowKey"
        :checked="checked"
        :disabled="checkboxDisabled"
        @change="emit('toggle')"
      />
    </div>
    <button
      type="button"
      class="item-action-btn-mobile"
      aria-label="Enviar revalidação"
      :disabled="disabled"
      @click="emit('submit')"
    >
      <IconArrowForward />
    </button>
    <div class="item-fields">
      <div class="item-status">
        <span class="item-status-label">Status</span>
        <SolTag
          :id="`revalidation-item-status-${index}`"
          :text="statusTag.text"
          variant="fill"
          size="small"
          :type="statusTag.type"
        />
      </div>
      <div class="item-name">
        <span class="item-name-label">Nome do projeto</span>
        <span class="item-name-value">{{ item.project_name }}</span>
      </div>
      <div class="item-brand">
        <span class="item-brand-label">Marca do inversor</span>
        <SolSelect
          :id="`revalidation-item-brand-${index}`"
          :selected="editState.brand"
          :name="`revalidation-item-brand-${index}`"
          size="small"
          :options="brandOptions"
          :disabled="disabled"
          @update:selected="onBrandUpdate"
        />
        <SolInputText
          v-if="editState.brand?.value === brandOtherValue"
          :id="`revalidation-item-brand-custom-${index}`"
          :model-value="editState.customBrand"
          :name="`revalidation-item-brand-custom-${index}`"
          size="small"
          placeholder="Digite a marca"
          :disabled="disabled"
          @update:model-value="onCustomBrandUpdate"
        />
      </div>
      <RevalidationSerialInput
        :model-value="editState.serials"
        :invalid-serials="editState.invalidSerials"
        :expected-total-inverters="item.expected_total_inverters"
        :disabled="disabled"
        class="item-serial"
        @update:model-value="onSerialsUpdate"
      />
      <div class="item-plant">
        <span class="item-plant-label">Nome da planta de monitoramento</span>
        <SolInputText
          :id="`revalidation-item-plant-${index}`"
          :model-value="editState.plantName"
          :name="`revalidation-item-plant-${index}`"
          size="small"
          placeholder="Cole aqui o nome da planta"
          :disabled="disabled"
          @update:model-value="onPlantNameUpdate"
        >
          <template #icon:right>
            <SolTooltip class="item-plant-paste-tooltip" text="Colar nome da planta" position="top">
              <button
                type="button"
                class="item-plant-paste-btn"
                aria-label="Colar nome da planta"
                :disabled="disabled"
                @click="handlePaste"
              >
                <IconContentPaste class="item-plant-paste-icon" aria-hidden="true" />
              </button>
            </SolTooltip>
          </template>
        </SolInputText>
        <p class="item-plant-help">
          Copie o nome da planta no portal
          <template v-for="p in [portal]" :key="p.href ?? p.name">
            <a
              v-if="p.href"
              :href="p.href"
              target="_blank"
              rel="noopener noreferrer"
              class="item-plant-portal-link"
            >{{ p.name }}</a>
            <span v-else class="item-plant-portal-name">{{ p.name }}</span>
          </template>
          e cole aqui.
        </p>
      </div>
    </div>
    <button
      type="button"
      class="item-action-btn"
      aria-label="Enviar revalidação"
      :disabled="disabled"
      @click="emit('submit')"
    >
      <IconArrowForward />
    </button>
  </li>
</template>

<style scoped lang="scss">
.revalidation-item {
  @apply w-full gap-4;
  @apply px-4 pt-4 pb-5;
  @apply rounded-md border-a-xs border-neutral-high-medium;
  @apply bg-neutral-high-pure;
  @apply grid;

  grid-template-columns: auto 1fr;
  align-items: center;

  &--disabled {
    @apply bg-neutral-high-light;
  }

  > .item-checkbox-area {
    grid-column: 1;
    grid-row: 1;
    @apply flex items-center min-w-0;

    :deep(.item-checkbox-tooltip-wrap) {
      @apply inline-flex items-center flex-shrink-0;
    }
  }

  > .item-action-btn-mobile {
    grid-column: 2;
    grid-row: 1;
    justify-self: end;
    @apply flex items-center justify-center;
    @apply w-xs h-xs;
    @apply rounded-md;
    @apply bg-neutral-low-dark text-neutral-high-pure;
    @apply border-0 p-0;
    @apply cursor-pointer;

    &:disabled {
      @apply opacity-40 cursor-not-allowed;
    }
  }

  > .item-action-btn {
    @apply hidden items-center justify-center;
    @apply w-xs h-xs;
    @apply flex-shrink-0 ml-auto self-center;
    @apply rounded-md;
    @apply bg-neutral-low-dark text-neutral-high-pure;
    @apply border-0 p-0;
    @apply cursor-pointer;

    &:disabled {
      @apply opacity-40 cursor-not-allowed;
    }
  }

  > .item-fields {
    grid-column: 1 / -1;
    grid-row: 2;
    @apply flex flex-col gap-4 w-full;

    @screen md:system {
      @apply flex-row items-start gap-6 flex-1;
    }

    > .item-status {
      @apply flex flex-col gap-2;
      @apply w-full;

      @screen md:system {
        @apply w-[160px] flex-shrink-0;
      }

      > .item-status-label {
        @apply fonts-body-small-bold;
        @apply text-neutral-low-dark;
      }
    }

    > .item-serial {
      @apply w-full;

      @screen md:system {
        @apply flex-1 min-w-[200px];
      }
    }

    > .item-plant {
      @apply flex flex-col gap-2;
      @apply w-full;

      @screen md:system {
        @apply flex-1 min-w-[180px];
      }

      > .item-plant-label {
        @apply fonts-body-small-bold;
        @apply text-neutral-low-dark;
      }

      :deep(.item-plant-paste-tooltip) {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        width: fit-content;
      }

      :deep(.item-plant-paste-tooltip > .spin.top) {
        bottom: calc(100% + 2px) !important;
        left: 50% !important;
        right: auto !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        --tw-translate-x: -50%;
      }

      :deep(.item-plant-paste-tooltip .tooltip.top) {
        left: 50% !important;
        right: auto !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        --tw-translate-x: -50%;
      }

      :deep(.item-plant-paste-btn) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent;
        cursor: pointer;
        color: inherit;
      }

      :deep(.item-plant-paste-icon) {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
        @apply text-neutral-low-dark;
      }

      > .item-plant-help {
        @apply m-0;
        @apply fonts-body-small-regular;
        @apply text-neutral-low-light;

        > .item-plant-portal-link {
          @apply text-neutral-low-light;
          @apply font-bold underline;
          text-underline-offset: auto;
        }

        > .item-plant-portal-name {
          @apply text-neutral-low-light;
          @apply font-bold;
        }
      }
    }

    > .item-brand {
      @apply flex flex-col gap-2;
      @apply w-full;

      @screen md:system {
        @apply w-[200px] flex-shrink-0;
      }

      > .item-brand-label {
        @apply fonts-body-small-bold;
        @apply text-neutral-low-dark;
      }
    }

    > .item-name {
      @apply flex flex-col;
      @apply gap-[10px];
      @apply w-full;

      @screen md:system {
        @apply w-[180px] flex-shrink-0;
      }

      > .item-name-label {
        @apply fonts-body-small-bold;
        @apply text-neutral-low-dark;
      }

      > .item-name-value {
        @apply fonts-body-medium-regular;
        @apply text-neutral-low-medium;
      }
    }
  }

  @screen md:system {
    @apply flex flex-row items-start px-6;

    > .item-checkbox-area {
      @apply self-center flex-shrink-0;
    }

    > .item-action-btn-mobile {
      @apply hidden;
    }

    > .item-action-btn {
      @apply flex;
    }
  }
}
</style>
